import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Plus, Pencil, Trash2, Library, Loader2, ArrowUp, ArrowDown, FileText, Sparkles } from "lucide-react";
import { SafetyCategory, SafetyItem } from "./types";

interface Props { templateId: string; locked: boolean; }

const emptyCat = { title: "", description: "", is_required: true, sort_order: 0 };
const emptyItem = {
  title: "", content: "", risk_description: "", protective_measures: "",
  employee_behavior: "", employee_confirmation_text: "",
  minimum_read_time_seconds: 10, sort_order: 0, is_required: true,
};

export const SafetyCategoriesManager = ({ templateId, locked }: Props) => {
  const [cats, setCats] = useState<SafetyCategory[]>([]);
  const [items, setItems] = useState<Record<string, SafetyItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [catDialog, setCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState<SafetyCategory | null>(null);
  const [catForm, setCatForm] = useState(emptyCat);
  const [libraryDialog, setLibraryDialog] = useState(false);
  const [library, setLibrary] = useState<SafetyCategory[]>([]);
  const [librarySelected, setLibrarySelected] = useState<Set<string>>(new Set());
  const [libraryImporting, setLibraryImporting] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [itemCategory, setItemCategory] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<SafetyItem | null>(null);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [saving, setSaving] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generatingCat, setGeneratingCat] = useState<string | null>(null);

  const generateQuestions = async (opts: { categoryId?: string; all?: boolean }) => {
    if (opts.all) setGeneratingAll(true); else if (opts.categoryId) setGeneratingCat(opts.categoryId);
    try {
      const payload: any = opts.all ? { template_id: templateId, replace: true } : { category_id: opts.categoryId, replace: true };
      const { data, error } = await supabase.functions.invoke("generate-safety-questions", { body: payload });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const generated = (data as any)?.generated ?? 0;
      const categories = (data as any)?.categories ?? 0;
      toast.success(`${generated} Fragen für ${categories} Kategorie${categories === 1 ? "" : "n"} generiert`);
    } catch (e: any) {
      toast.error(e.message || "Fragen-Generierung fehlgeschlagen");
    } finally {
      setGeneratingAll(false); setGeneratingCat(null);
    }
  };

  const load = async () => {
    setLoading(true);
    const { data: cs } = await supabase
      .from("safety_instruction_categories")
      .select("*")
      .eq("template_id", templateId)
      .order("sort_order");
    const catsList = (cs as unknown as SafetyCategory[]) || [];
    setCats(catsList);
    if (catsList.length) {
      const { data: its } = await supabase
        .from("safety_instruction_items")
        .select("*")
        .in("category_id", catsList.map((c) => c.id))
        .order("sort_order");
      const grouped: Record<string, SafetyItem[]> = {};
      (its as unknown as SafetyItem[] || []).forEach((it) => {
        grouped[it.category_id] = grouped[it.category_id] || [];
        grouped[it.category_id].push(it);
      });
      setItems(grouped);
    } else {
      setItems({});
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [templateId]);

  const openCatDialog = (c?: SafetyCategory) => {
    if (c) {
      setEditingCat(c);
      setCatForm({ title: c.title, description: c.description || "", is_required: c.is_required, sort_order: c.sort_order });
    } else {
      setEditingCat(null);
      setCatForm({ ...emptyCat, sort_order: (cats[cats.length - 1]?.sort_order ?? 0) + 10 });
    }
    setCatDialog(true);
  };

  const saveCat = async () => {
    if (!catForm.title.trim()) { toast.error("Titel erforderlich"); return; }
    setSaving(true);
    try {
      if (editingCat) {
        const { error } = await supabase.from("safety_instruction_categories").update({
          title: catForm.title, description: catForm.description || null,
          is_required: catForm.is_required, sort_order: catForm.sort_order,
        }).eq("id", editingCat.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("safety_instruction_categories").insert({
          template_id: templateId,
          title: catForm.title, description: catForm.description || null,
          is_required: catForm.is_required, sort_order: catForm.sort_order,
          is_library: false,
        });
        if (error) throw error;
      }
      toast.success("Gespeichert");
      setCatDialog(false);
      load();
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  };

  const deleteCat = async (id: string) => {
    if (!confirm("Kategorie löschen?")) return;
    const { error } = await supabase.from("safety_instruction_categories").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Gelöscht"); load(); }
  };

  const move = async (cat: SafetyCategory, dir: -1 | 1) => {
    const idx = cats.findIndex((c) => c.id === cat.id);
    const swap = cats[idx + dir];
    if (!swap) return;
    await supabase.from("safety_instruction_categories").update({ sort_order: swap.sort_order }).eq("id", cat.id);
    await supabase.from("safety_instruction_categories").update({ sort_order: cat.sort_order }).eq("id", swap.id);
    load();
  };

  const openLibrary = async () => {
    const { data } = await supabase
      .from("safety_instruction_categories")
      .select("*").eq("is_library", true).order("sort_order");
    setLibrary((data as unknown as SafetyCategory[]) || []);
    setLibrarySelected(new Set());
    setLibraryDialog(true);
  };

  const toggleLibrarySelect = (id: string) => {
    setLibrarySelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const importSelectedFromLibrary = async () => {
    if (librarySelected.size === 0) return;
    setLibraryImporting(true);
    try {
      let baseSort = (cats[cats.length - 1]?.sort_order ?? 0);
      const selectedLib = library.filter(c => librarySelected.has(c.id));

      // Load source items for selected library categories
      const { data: srcItems, error: itemsErr } = await supabase
        .from("safety_instruction_items")
        .select("*")
        .in("category_id", selectedLib.map(c => c.id))
        .order("sort_order");
      if (itemsErr) throw itemsErr;

      let itemsCopied = 0;
      for (const libCat of selectedLib) {
        baseSort += 10;
        const { data: inserted, error: catErr } = await supabase
          .from("safety_instruction_categories")
          .insert({
            template_id: templateId,
            title: libCat.title,
            description: libCat.description,
            sort_order: baseSort,
            is_required: true,
            is_active: true,
            is_library: false,
          })
          .select("id")
          .single();
        if (catErr) throw catErr;

        const itemsForCat = (srcItems || []).filter((it: any) => it.category_id === libCat.id);
        if (itemsForCat.length) {
          const rows = itemsForCat.map((it: any) => ({
            category_id: inserted.id,
            title: it.title,
            content: it.content,
            risk_description: it.risk_description,
            protective_measures: it.protective_measures,
            employee_behavior: it.employee_behavior,
            employee_confirmation_text: it.employee_confirmation_text,
            minimum_read_time_seconds: it.minimum_read_time_seconds,
            sort_order: it.sort_order,
            is_required: it.is_required,
            is_active: true,
          }));
          const { error: insErr } = await supabase.from("safety_instruction_items").insert(rows);
          if (insErr) throw insErr;
          itemsCopied += rows.length;
        }
      }
      toast.success(`${selectedLib.length} Kategorie${selectedLib.length === 1 ? "" : "n"} mit ${itemsCopied} Punkt${itemsCopied === 1 ? "" : "en"} hinzugefügt`);
      setLibraryDialog(false);
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLibraryImporting(false);
    }
  };

  // ITEM CRUD
  const openItemDialog = (catId: string, it?: SafetyItem) => {
    setItemCategory(catId);
    if (it) {
      setEditingItem(it);
      setItemForm({
        title: it.title, content: it.content || "", risk_description: it.risk_description || "",
        protective_measures: it.protective_measures || "", employee_behavior: it.employee_behavior || "",
        employee_confirmation_text: it.employee_confirmation_text || "",
        minimum_read_time_seconds: it.minimum_read_time_seconds, sort_order: it.sort_order,
        is_required: it.is_required,
      });
    } else {
      setEditingItem(null);
      const last = items[catId]?.[items[catId].length - 1];
      setItemForm({ ...emptyItem, sort_order: (last?.sort_order ?? 0) + 10 });
    }
    setItemDialog(true);
  };

  const saveItem = async () => {
    if (!itemCategory || !itemForm.title.trim()) { toast.error("Titel erforderlich"); return; }
    setSaving(true);
    try {
      if (editingItem) {
        const { error } = await supabase.from("safety_instruction_items").update({
          title: itemForm.title, content: itemForm.content || null,
          risk_description: itemForm.risk_description || null,
          protective_measures: itemForm.protective_measures || null,
          employee_behavior: itemForm.employee_behavior || null,
          employee_confirmation_text: itemForm.employee_confirmation_text || null,
          minimum_read_time_seconds: itemForm.minimum_read_time_seconds,
          sort_order: itemForm.sort_order, is_required: itemForm.is_required,
        }).eq("id", editingItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("safety_instruction_items").insert({
          category_id: itemCategory,
          title: itemForm.title, content: itemForm.content || null,
          risk_description: itemForm.risk_description || null,
          protective_measures: itemForm.protective_measures || null,
          employee_behavior: itemForm.employee_behavior || null,
          employee_confirmation_text: itemForm.employee_confirmation_text || null,
          minimum_read_time_seconds: itemForm.minimum_read_time_seconds,
          sort_order: itemForm.sort_order, is_required: itemForm.is_required,
        });
        if (error) throw error;
      }
      toast.success("Gespeichert");
      setItemDialog(false);
      load();
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  };

  const deleteItem = async (id: string) => {
    if (!confirm("Unterweisungspunkt löschen?")) return;
    const { error } = await supabase.from("safety_instruction_items").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Gelöscht"); load(); }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">Kategorien &amp; Unterweisungspunkte</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={openLibrary} disabled={locked} className="gap-2">
              <Library className="h-4 w-4" /> Aus Bibliothek
            </Button>
            <Button size="sm" variant="outline" onClick={() => generateQuestions({ all: true })} disabled={locked || generatingAll || cats.length === 0} className="gap-2">
              {generatingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Alle Fragen generieren
            </Button>
            <Button size="sm" onClick={() => openCatDialog()} disabled={locked} className="gap-2">
              <Plus className="h-4 w-4" /> Neue Kategorie
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {cats.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground text-sm">Noch keine Kategorien. Füge welche aus der Bibliothek hinzu oder erstelle neue.</p>
        ) : (
          <Accordion type="multiple" className="space-y-2">
            {cats.map((c, idx) => (
              <AccordionItem key={c.id} value={c.id} className="border rounded-lg px-3">
                <div className="flex items-center gap-2">
                  <AccordionTrigger className="flex-1 hover:no-underline">
                    <div className="flex items-center gap-2 text-left">
                      <span className="font-medium">{c.title}</span>
                      <Badge variant="outline" className="text-xs">{(items[c.id] || []).length} Punkte</Badge>
                      {!c.is_required && <Badge variant="secondary" className="text-xs">optional</Badge>}
                    </div>
                  </AccordionTrigger>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button size="icon" variant="ghost" disabled={locked || idx === 0} onClick={() => move(c, -1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" disabled={locked || idx === cats.length - 1} onClick={() => move(c, 1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" title="Fragen automatisch generieren" disabled={locked || generatingCat === c.id || generatingAll} onClick={() => generateQuestions({ categoryId: c.id })}>
                      {generatingCat === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    </Button>
                    <Button size="icon" variant="ghost" disabled={locked} onClick={() => openCatDialog(c)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" disabled={locked} onClick={() => deleteCat(c.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                </div>
                <AccordionContent>
                  {c.description && <p className="text-sm text-muted-foreground mb-3">{c.description}</p>}
                  <div className="space-y-2">
                    {(items[c.id] || []).map((it) => (
                      <div key={it.id} className="border rounded p-3 flex items-start justify-between gap-2 bg-muted/30">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium text-sm">{it.title}</span>
                            <Badge variant="outline" className="text-xs">{it.minimum_read_time_seconds}s lesen</Badge>
                          </div>
                          {it.content && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{it.content}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" disabled={locked} onClick={() => openItemDialog(c.id, it)}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button size="icon" variant="ghost" disabled={locked} onClick={() => deleteItem(it.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                        </div>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" disabled={locked} onClick={() => openItemDialog(c.id)} className="gap-2">
                      <Plus className="h-3.5 w-3.5" /> Punkt hinzufügen
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>

      {/* Cat dialog */}
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingCat ? "Kategorie bearbeiten" : "Neue Kategorie"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titel *</Label><Input value={catForm.title} onChange={(e) => setCatForm({ ...catForm, title: e.target.value })} /></div>
            <div><Label>Beschreibung</Label><Textarea value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} /></div>
            <div className="flex items-center justify-between"><Label>Pflichtkategorie</Label><Switch checked={catForm.is_required} onCheckedChange={(c) => setCatForm({ ...catForm, is_required: c })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)}>Abbrechen</Button>
            <Button onClick={saveCat} disabled={saving}>{saving ? "..." : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Library dialog */}
      <Dialog open={libraryDialog} onOpenChange={setLibraryDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Standard-Kategorien-Bibliothek</DialogTitle>
            <p className="text-xs text-muted-foreground">Wähle einen oder mehrere Einträge aus und füge sie gemeinsam hinzu.</p>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 pb-2 border-b">
            <div className="text-xs text-muted-foreground">{librarySelected.size} ausgewählt</div>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setLibrarySelected(new Set(library.map(l => l.id)))}>Alle</Button>
              <Button size="sm" variant="ghost" onClick={() => setLibrarySelected(new Set())}>Keine</Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {library.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Keine Bibliothekseinträge gefunden.</p>}
            {library.map((c) => {
              const checked = librarySelected.has(c.id);
              return (
                <label
                  key={c.id}
                  className={`border rounded p-3 flex items-start gap-3 cursor-pointer transition-colors ${checked ? "bg-primary/5 border-primary/40" : "hover:bg-muted/40"}`}
                >
                  <Switch checked={checked} onCheckedChange={() => toggleLibrarySelect(c.id)} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{c.title}</p>
                    {c.description && <p className="text-xs text-muted-foreground mt-1">{c.description}</p>}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLibraryDialog(false)}>Abbrechen</Button>
            <Button onClick={importSelectedFromLibrary} disabled={librarySelected.size === 0 || libraryImporting} className="gap-2">
              {libraryImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {librarySelected.size > 0 ? `${librarySelected.size} hinzufügen` : "Hinzufügen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingItem ? "Punkt bearbeiten" : "Neuer Unterweisungspunkt"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titel *</Label><Input value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} /></div>
            <div><Label>Unterweisungstext / Inhalt</Label><Textarea rows={4} value={itemForm.content} onChange={(e) => setItemForm({ ...itemForm, content: e.target.value })} /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Risiko / Gefährdung</Label><Textarea rows={3} value={itemForm.risk_description} onChange={(e) => setItemForm({ ...itemForm, risk_description: e.target.value })} /></div>
              <div><Label>Schutzmaßnahme</Label><Textarea rows={3} value={itemForm.protective_measures} onChange={(e) => setItemForm({ ...itemForm, protective_measures: e.target.value })} /></div>
            </div>
            <div><Label>Verhalten des Mitarbeiters</Label><Textarea rows={2} value={itemForm.employee_behavior} onChange={(e) => setItemForm({ ...itemForm, employee_behavior: e.target.value })} /></div>
            <div><Label>Pflichtbestätigungstext</Label><Textarea rows={2} value={itemForm.employee_confirmation_text} onChange={(e) => setItemForm({ ...itemForm, employee_confirmation_text: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mindestlesezeit (s)</Label><Input type="number" min={0} value={itemForm.minimum_read_time_seconds} onChange={(e) => setItemForm({ ...itemForm, minimum_read_time_seconds: parseInt(e.target.value || "0") })} /></div>
              <div className="flex items-end justify-between"><Label>Pflicht</Label><Switch checked={itemForm.is_required} onCheckedChange={(c) => setItemForm({ ...itemForm, is_required: c })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemDialog(false)}>Abbrechen</Button>
            <Button onClick={saveItem} disabled={saving}>{saving ? "..." : "Speichern"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
